import { useCallback, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { navigationV2 } from "@src/shared/navigation/v2";
import { toErrorMessage } from "@src/shared/utils/errors";
import { events } from "@src/shared/utils/observability/events";

import { authClient, clearTwoFactorChallengeTokens } from "../../data";
import type {
  TwoFactorChallengeValues,
  TwoFactorMethod,
} from "../../domain/v2";
import { waitForSession } from "../waitForSession";
import { resolvePostAuthDestinationV2 } from "./resolvePostAuthDestinationV2";
import { trackEmailLoginSuccess } from "./trackEmailLoginSuccess";

const FALLBACK_ERROR = "That code didn't match. Try again.";

/**
 * Better Auth's code for a challenge that has expired, been consumed, or was
 * never established — distinct from a code that simply didn't match.
 */
const INVALID_CHALLENGE_ERROR_CODE = "INVALID_TWO_FACTOR_COOKIE";
const EXPIRED_CHALLENGE_MESSAGE =
  "Your sign-in request expired. Please sign in again.";

/**
 * Drives the `/2fa` page: the second half of an email/password sign-in that the
 * server answered with `twoFactorRedirect`. The two-factor cookie set by that
 * response is what identifies the user here, so this flow needs no session of
 * its own until verification succeeds.
 */
export function useTwoFactorChallengeFlow() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [method, setMethod] = useState<TwoFactorMethod>("totp");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingEmailCode, setIsSendingEmailCode] = useState(false);
  const [emailCodeSent, setEmailCodeSent] = useState(false);

  const redirectTo = searchParams.get("redirectTo");

  const selectMethod = useCallback((next: TwoFactorMethod) => {
    setMethod(next);
    setError(null);

    // The "we emailed you a code" banner only makes sense while the emailed code
    // is the one being asked for.
    if (next !== "otp") setEmailCodeSent(false);
  }, []);

  /**
   * A dead challenge can surface on any of these calls, and it is not something
   * the user can retry their way out of: the form can never succeed again. Clear
   * the tokens and send them back to sign in, keeping `redirectTo` so the
   * original destination survives the round trip.
   */
  const recoverFromDeadChallenge = useCallback(() => {
    clearTwoFactorChallengeTokens();

    const params = new URLSearchParams({ error: EXPIRED_CHALLENGE_MESSAGE });

    if (redirectTo) params.set("redirectTo", redirectTo);

    navigate(navigationV2.login(params), { replace: true });
  }, [navigate, redirectTo]);

  const sendEmailCode = useCallback(async () => {
    setIsSendingEmailCode(true);
    setError(null);

    try {
      const result = await authClient.twoFactor.sendOtp();

      if (result.error) {
        if (result.error.code === INVALID_CHALLENGE_ERROR_CODE) {
          recoverFromDeadChallenge();
          return;
        }

        setError(result.error.message?.trim() || "Unable to email a code.");
        return;
      }

      setEmailCodeSent(true);
      setMethod("otp");
    } catch (err) {
      setError(toErrorMessage(err));
    } finally {
      setIsSendingEmailCode(false);
    }
  }, [recoverFromDeadChallenge]);

  const verify = useCallback(
    async ({ code, trustDevice }: TwoFactorChallengeValues) => {
      setIsSubmitting(true);
      setError(null);

      try {
        const result = await (method === "otp"
          ? authClient.twoFactor.verifyOtp({ code, trustDevice })
          : method === "backup_code"
            ? authClient.twoFactor.verifyBackupCode({ code, trustDevice })
            : authClient.twoFactor.verifyTotp({ code, trustDevice }));

        if (result.error) {
          // A dead challenge is not a wrong code, so don't tell the user to try
          // again on a form that can never succeed.
          if (result.error.code === INVALID_CHALLENGE_ERROR_CODE) {
            events.verifyTwoFactor.track({
              method,
              success: false,
              error: INVALID_CHALLENGE_ERROR_CODE,
            });
            recoverFromDeadChallenge();
            return;
          }

          const message = result.error.message?.trim() || FALLBACK_ERROR;

          setError(message);
          events.verifyTwoFactor.track({
            method,
            success: false,
            error: message,
          });
          return;
        }

        events.verifyTwoFactor.track({ method, success: true });
        // Single-use server-side; drop our copy so a stale token can't ride
        // along on the next sign-in attempt.
        clearTwoFactorChallengeTokens();

        await Promise.all([trackEmailLoginSuccess(), waitForSession()]);
        const destination = await resolvePostAuthDestinationV2({
          fallback: navigationV2.home(),
          operation: "twoFactorVerify",
          redirectTo,
          route: "v2/2fa",
        });

        navigate(destination, { replace: true });
      } catch (err) {
        const message = toErrorMessage(err);

        setError(message);
        events.verifyTwoFactor.track({
          method,
          success: false,
          error: message,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [method, navigate, recoverFromDeadChallenge, redirectTo],
  );

  return {
    emailCodeSent,
    error,
    isSendingEmailCode,
    isSubmitting,
    method,
    selectMethod,
    sendEmailCode,
    verify,
  } as const;
}
